/**
 * Number an indented outline.
 *
 * Depth comes from indentation (tabs, spaces or Markdown bullets). It is read with a
 * stack of the indents seen so far rather than a fixed step, so two-space, four-space
 * and tab outlines all work, and a stray extra space does not create a phantom level.
 * Labels are then generated from per-style level specifications.
 */

export type OutlineStyle =
  | "decimal"
  | "alpha"
  | "legalClause"
  | "legalArticle"
  | "legalStatute";

export const OUTLINE_STYLES: readonly OutlineStyle[] = [
  "decimal",
  "alpha",
  "legalClause",
  "legalArticle",
  "legalStatute",
];

export type OutlineIndent = "none" | "two" | "four" | "tab";

/** The words some legal styles print in front of a number. */
export interface OutlineWords {
  article: string;
  section: string;
  sec: string;
}

export interface OutlineOptions {
  style: OutlineStyle;
  tabWidth: number;
  indent: OutlineIndent;
  /** Remove numbers or letters that are already at the front of each line. */
  stripLabels: boolean;
  /** The number the first top-level item gets. */
  start: number;
  /** Decimal style only: write `1.` and `1.1.` instead of `1` and `1.1`. */
  trailingDot: boolean;
  words: OutlineWords;
}

export const DEFAULT_OUTLINE_WORDS: OutlineWords = {
  article: "Article",
  section: "Section",
  sec: "Sec.",
};

export const DEFAULT_OUTLINE_OPTIONS: OutlineOptions = {
  style: "alpha",
  tabWidth: 4,
  indent: "two",
  stripLabels: false,
  start: 1,
  trailingDot: false,
  words: DEFAULT_OUTLINE_WORDS,
};

// ---------------------------------------------------------------------------
// Counters

export type Counter =
  | "decimal"
  | "upperRoman"
  | "lowerRoman"
  | "upperAlpha"
  | "lowerAlpha"
  | "upperDouble"
  | "lowerDouble";

const ROMAN: ReadonlyArray<readonly [number, string]> = [
  [1000, "M"],
  [900, "CM"],
  [500, "D"],
  [400, "CD"],
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];
const ROMAN_MAX = 3999;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ALPHABET_LENGTH = ALPHABET.length;

/** Roman numerals to 3,999; above that, ordinary digits. */
export function toRoman(value: number): string {
  if (value < 1 || value > ROMAN_MAX) {
    return String(value);
  }
  let rest = value;
  let out = "";
  for (const [size, symbol] of ROMAN) {
    while (rest >= size) {
      out += symbol;
      rest -= size;
    }
  }
  return out;
}

/** A, B … Z, AA, BB … ZZ, AAA: the letter repeated, as word processors count. */
export function toAlpha(value: number): string {
  if (value < 1) {
    return String(value);
  }
  const letter = ALPHABET[(value - 1) % ALPHABET_LENGTH] ?? "A";
  return letter.repeat(Math.floor((value - 1) / ALPHABET_LENGTH) + 1);
}

/** The statute style's (aa), (bb), (cc): every item has a doubled letter, (aaa) after (zz). */
export function toDoubleAlpha(value: number): string {
  if (value < 1) {
    return String(value);
  }
  const letter = ALPHABET[(value - 1) % ALPHABET_LENGTH] ?? "A";
  return letter.repeat(Math.floor((value - 1) / ALPHABET_LENGTH) + 2);
}

export function formatCounter(value: number, counter: Counter): string {
  switch (counter) {
    case "upperRoman":
      return toRoman(value);
    case "lowerRoman":
      return toRoman(value).toLowerCase();
    case "upperAlpha":
      return toAlpha(value);
    case "lowerAlpha":
      return toAlpha(value).toLowerCase();
    case "upperDouble":
      return toDoubleAlpha(value);
    case "lowerDouble":
      return toDoubleAlpha(value).toLowerCase();
    default:
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Styles

/**
 * One level of a style. A level is its own counter between `before` and `after`
 * (`(` and `)`, or nothing and `.`), unless `dotted` is set: then it is the numbers of
 * every level down from `from`, joined with full stops, with the last one zero-padded
 * to `pad` digits (Section 1.01).
 */
interface LevelSpec {
  counter: Counter;
  before: string;
  after: string;
  dotted?: { from: number; pad: number };
  /** Key of a word from `OutlineWords` to print before the number. */
  word?: keyof OutlineWords;
}

const spec = (
  counter: Counter,
  before: string,
  after: string,
  extra: Partial<LevelSpec> = {}
): LevelSpec => ({ counter, before, after, ...extra });

const PAREN_ALPHA = spec("lowerAlpha", "(", ")");
const PAREN_ROMAN = spec("lowerRoman", "(", ")");
const PAREN_UPPER = spec("upperAlpha", "(", ")");

export const STYLE_LEVELS: Record<
  Exclude<OutlineStyle, "decimal">,
  readonly LevelSpec[]
> = {
  alpha: [
    spec("upperRoman", "", "."),
    spec("upperAlpha", "", "."),
    spec("decimal", "", "."),
    spec("lowerAlpha", "", "."),
    spec("decimal", "(", ")"),
    PAREN_ALPHA,
    PAREN_ROMAN,
  ],
  legalClause: [
    spec("decimal", "", "."),
    spec("decimal", "", "", { dotted: { from: 0, pad: 0 } }),
    PAREN_ALPHA,
    PAREN_ROMAN,
    PAREN_UPPER,
  ],
  legalArticle: [
    spec("upperRoman", "", "", { word: "article" }),
    spec("decimal", "", "", { dotted: { from: 0, pad: 2 }, word: "section" }),
    PAREN_ALPHA,
    PAREN_ROMAN,
    PAREN_UPPER,
  ],
  legalStatute: [
    spec("decimal", "", ".", { word: "sec" }),
    PAREN_ALPHA,
    spec("decimal", "(", ")"),
    PAREN_UPPER,
    PAREN_ROMAN,
    spec("upperRoman", "(", ")"),
    spec("lowerDouble", "(", ")"),
    spec("upperDouble", "(", ")"),
  ],
};

function levelSpecs(style: OutlineStyle): readonly LevelSpec[] {
  return style === "decimal" ? [] : STYLE_LEVELS[style];
}

/** How many levels the style defines before its pattern starts again (decimal never does). */
export function definedLevels(style: OutlineStyle): number {
  return style === "decimal"
    ? Number.POSITIVE_INFINITY
    : levelSpecs(style).length;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

function buildLabel(
  levelSpec: LevelSpec,
  counters: readonly number[],
  level: number,
  options: { words: OutlineWords; wrapped: boolean }
): string {
  const word = levelSpec.word ? `${options.words[levelSpec.word]} ` : "";
  const dotted = options.wrapped ? undefined : levelSpec.dotted;
  if (dotted) {
    const parts = counters
      .slice(dotted.from, level + 1)
      .map((value, index, all) =>
        index === all.length - 1 ? pad(value, dotted.pad) : String(value)
      );
    return `${word}${levelSpec.before}${parts.join(".")}${levelSpec.after}`;
  }
  const value = counters[level] ?? 1;
  return `${word}${levelSpec.before}${formatCounter(value, levelSpec.counter)}${levelSpec.after}`;
}

function decimalLabel(
  counters: readonly number[],
  level: number,
  trailingDot: boolean
): string {
  const path = counters.slice(0, level + 1).join(".");
  return trailingDot ? `${path}.` : path;
}

// ---------------------------------------------------------------------------
// Reading the outline

const RE_LINE_BREAK = /\r\n|\r|\n/;
const RE_LEADING_SPACE = /^[ \t ]*/;
const RE_BULLET = /^[-*+•–◦▪]\s+/;
const RE_TRAILING_SPACE = /[ \t ]+$/;
const ROMAN_UPPER =
  "(?=[IVXLCDM])M{0,3}(?:CM|CD|D?C{0,3})(?:XC|XL|L?X{0,3})(?:IX|IV|V?I{0,3})";
const ROMAN_LOWER =
  "(?=[ivxlcdm])m{0,3}(?:cm|cd|d?c{0,3})(?:xc|xl|l?x{0,3})(?:ix|iv|v?i{0,3})";
/**
 * A label already at the front of a line: `1.`, `1)`, `1.2.3`, `I.`, `a)`, `(a)`, `(iv)`.
 * A single letter or a valid Roman numeral needs its dot or bracket, so a line starting
 * "A tale of" or "Did." is left alone, though "A. Smith" and "3.5 million" are not.
 */
const RE_EXISTING_LABEL = new RegExp(
  `^(?:\\d+(?:\\.\\d+)+\\.?|\\d+[.)]|\\((?:\\d+|[A-Za-z]|${ROMAN_UPPER}|${ROMAN_LOWER})\\)|(?:${ROMAN_UPPER}|${ROMAN_LOWER}|[A-Za-z])[.)])\\s+`
);
/** `Article IV`, `Section 2.01`, `Sec. 3.` — what the legal styles write. */
const RE_WORD_LABEL =
  /^(?:article\s+(?:[IVXLCDM]+|\d+)|section\s+\d+(?:\.\d+)*|sec\.\s*\d+(?:\.\d+)*)\.?\s+/i;

export interface OutlineEntry {
  /** 1-based line number in the input. */
  line: number;
  level: number;
  text: string;
  /** The indent did not match any earlier one exactly and was rounded to a level. */
  snapped: boolean;
}

function indentWidth(space: string, tabWidth: number): number {
  let width = 0;
  for (const char of space) {
    width =
      char === "\t" ? (Math.floor(width / tabWidth) + 1) * tabWidth : width + 1;
  }
  return width;
}

/** Place `indent` among the open levels; returns its level and whether it was rounded. */
function placeIndent(
  stack: number[],
  indent: number
): { level: number; snapped: boolean } {
  let popped = false;
  while (stack.length > 0 && (stack.at(-1) ?? 0) > indent) {
    stack.pop();
    popped = true;
  }
  if (stack.at(-1) === indent) {
    return { level: stack.length - 1, snapped: false };
  }
  stack.push(indent);
  // Backing out to an indent no earlier line used is a rounding; going deeper is not.
  return { level: stack.length - 1, snapped: popped && stack.length > 1 };
}

export function readOutline(
  input: string,
  options: Pick<OutlineOptions, "tabWidth" | "stripLabels">
): OutlineEntry[] {
  const entries: OutlineEntry[] = [];
  const stack: number[] = [];
  const tabWidth = Math.max(1, Math.floor(options.tabWidth));
  input.split(RE_LINE_BREAK).forEach((raw, index) => {
    const line = raw.replace(RE_TRAILING_SPACE, "");
    if (line.trim() === "") {
      return;
    }
    const space = line.match(RE_LEADING_SPACE)?.[0] ?? "";
    let text = line.slice(space.length).replace(RE_BULLET, "");
    if (options.stripLabels) {
      text = text.replace(RE_WORD_LABEL, "").replace(RE_EXISTING_LABEL, "");
    }
    const { level, snapped } = placeIndent(stack, indentWidth(space, tabWidth));
    entries.push({ line: index + 1, level, text: text.trim(), snapped });
  });
  return entries;
}

// ---------------------------------------------------------------------------
// Numbering

export interface NumberedItem extends OutlineEntry {
  label: string;
}

export interface OutlineResult {
  items: NumberedItem[];
  output: string;
  /** Deepest level used, counting from 1. */
  depth: number;
  /** Items at each level, from level 1. */
  levelCounts: number[];
  /** The first label seen at each level, from level 1. */
  firstLabels: string[];
  /** Lines whose indent was rounded to fit the levels already open. */
  snapped: number;
  /** The outline is deeper than the style defines, so its pattern started again. */
  wrapped: boolean;
}

const INDENT_UNIT: Record<OutlineIndent, string> = {
  none: "",
  two: "  ",
  four: "    ",
  tab: "\t",
};

function labelFor(
  options: OutlineOptions,
  counters: readonly number[],
  level: number
): string {
  if (options.style === "decimal") {
    return decimalLabel(counters, level, options.trailingDot);
  }
  const specs = levelSpecs(options.style);
  const levelSpec = specs[level % specs.length];
  return levelSpec
    ? buildLabel(levelSpec, counters, level, {
        words: options.words,
        wrapped: level >= specs.length,
      })
    : "";
}

export function numberOutline(
  input: string,
  options: OutlineOptions
): OutlineResult {
  const entries = readOutline(input, options);
  const counters: number[] = [];
  const levelCounts: number[] = [];
  const firstLabels: string[] = [];
  const items: NumberedItem[] = [];
  const start = Math.max(1, Math.floor(options.start));
  for (const entry of entries) {
    counters.length = entry.level + 1;
    counters[entry.level] =
      (counters[entry.level] ?? (entry.level === 0 ? start - 1 : 0)) + 1;
    for (let level = 0; level < entry.level; level += 1) {
      counters[level] = counters[level] ?? 1;
    }
    const label = labelFor(options, counters, entry.level);
    levelCounts[entry.level] = (levelCounts[entry.level] ?? 0) + 1;
    firstLabels[entry.level] = firstLabels[entry.level] ?? label;
    items.push({ ...entry, label });
  }
  const unit = INDENT_UNIT[options.indent];
  const output = items
    .map((item) =>
      `${unit.repeat(item.level)}${item.label} ${item.text}`.trimEnd()
    )
    .join("\n");
  const depth = levelCounts.length;
  return {
    items,
    output,
    depth,
    levelCounts: Array.from(levelCounts, (count) => count ?? 0),
    firstLabels: Array.from(firstLabels, (label) => label ?? ""),
    snapped: items.filter((item) => item.snapped).length,
    wrapped: depth > definedLevels(options.style),
  };
}
